import { useEffect, useRef } from 'react';
import type Reaptcha from 'reaptcha';
import useResendingEnableTimeCookie from '~/client/components/2faManaging/hooks/useResendingEnableTimeCookie';
import useStepByStepAuthContext from '~/client/components/Auth/Authenticate/AuthSteps/context/hooks/useStepByStepAuthContext';
import useRouter from '~/client/hooks/router/useRouter';
import usePhoneNumberHint from '~/client/hooks/usePhoneNumberHint';
import useLoginError from '~/client/hooks/user/useLoginError';
import useSms2faCodeSubmit from './helpers/useSms2faCodeSubmit';
import useHandleLogin from '../../../hooks/useHandleLogin';
import useRedirectUrl from '../../../hooks/useRedirectUrl';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};

const useEnterSms2faStep = ({ onSuccess, isPopover }: Props) => {
  const { reload, locale, replace } = useRouter();
  const {
    loginCaptchaRequired: captchaRequired,
    loginSuccessful,
    control,
    errors,
    setErrors,
    clearErrors,
    setValue,
    register,
    required,
    getValues,
  } = useStepByStepAuthContext();

  const recaptchaRef = useRef<Reaptcha>(null);

  const {
    handleSubmit,
    handleCodeSubmit,
    handleResendCodeClick,
    authCodeErrorMessage,
  } = useSms2faCodeSubmit({ onSuccess, isPopover });

  const redirect = useRedirectUrl();
  const { loading } = useHandleLogin({ captchaRequired });
  const { error: loginError } = useLoginError();
  const { phoneHint } = usePhoneNumberHint();
  const { setResendingEnableTimeCookie } = useResendingEnableTimeCookie();

  useEffect(() => {
    setResendingEnableTimeCookie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loginError) {
      const error = {
        message: loginError,
      };
      setErrors?.([error] as GenericErrorType[]);
    }
    if (loginSuccessful) {
      isPopover ? reload() : replace(redirect);
    }
  }, [
    isPopover,
    locale,
    loginSuccessful,
    replace,
    redirect,
    reload,
    loginError,
    setErrors,
    setValue,
  ]);

  const uses2FA = getValues?.('uses2FA');

  return {
    control,
    errors,
    uses2FA,
    loading: loading || loginSuccessful,
    captchaRequired,
    recaptchaRef,
    clearErrors,
    register,
    required,
    phoneHint,
    handleCodeSubmit,
    authCodeErrorMessage,
    handleSubmit,
    handleResendCodeClick,
  };
};

export default useEnterSms2faStep;
